import {
  Feature,
  getFeatureByAddOn,
  getFeatureFromPlan,
  isSamePlanIgnoringEcom,
  LineItem,
  OrderPreviewRequest,
  OrderPreviewResponse,
  OrderProductPreview,
  PlanInterval,
  ProductPreviewRequest,
  RecurlyAddOn,
  Subscription,
  SubscriptionSource,
  SubscriptionPlanPrice,
} from '../../../../models';
import { getUnitPrice } from '../../../../hook';
import {
  DEFAULT_TRUE_BUNDLE_FEATURE,
  getTrueBundlePlan,
  TRUE_MORE_TESTING_PRODUCT_FEATURES,
  TRUE_BUNDLE_PURCHASE_FEATURES,
} from './trueBundlePlans';

const PAID_RECURLY_ADD_ONS = Object.values(RecurlyAddOn).filter(
  addOn => addOn !== RecurlyAddOn.TESTCLOUD_SESSION_CROSSBROWSER_FREE,
);

export const getTrueBundlePreviewRequests = (
  orderPreviewRequest?: OrderPreviewRequest,
  fallbackPreviewRequest?: ProductPreviewRequest,
): ProductPreviewRequest[] => {
  if (orderPreviewRequest?.singleProductList?.length) {
    return orderPreviewRequest.singleProductList;
  }

  return fallbackPreviewRequest ? [fallbackPreviewRequest] : [];
};

export const getPreviewRequestFeature = (
  request: ProductPreviewRequest,
): Feature => getFeatureFromPlan(request.productCode) ?? DEFAULT_TRUE_BUNDLE_FEATURE;

export const getPreviewProductFeature = (
  product: OrderProductPreview,
): Feature => getFeatureFromPlan(product.productCode) ?? product.feature;

export const getPreviewExistingQuantity = (
  product: Pick<OrderProductPreview, 'quantity' | 'newQuantity'>,
): number => Math.max(product.quantity - product.newQuantity, 0);

export const isMonthlyToAnnualSwitchPreview = (
  product: OrderProductPreview,
  currentSubscriptions: Subscription[] = [],
): boolean => {
  const feature = getPreviewProductFeature(product);

  return product.billingInterval === PlanInterval.YEAR
    && product.creditAmount > 0
    && currentSubscriptions.some(subscription => subscription.feature === feature
      && subscription.source === SubscriptionSource.RECURLY
      && subscription.billingInterval === PlanInterval.MONTH);
};

export const isMonthlyToAnnualSwitchRequest = (
  product: ProductPreviewRequest,
  currentSubscriptions: Subscription[] = [],
): boolean => {
  const feature = getPreviewRequestFeature(product);

  return product.quantity === 0
    && !!product.recurlySubscriptionUuid
    && PlanInterval.getIntervalFromPlanCode(product.productCode) === PlanInterval.YEAR
    && currentSubscriptions.some(subscription => subscription.feature === feature
      && subscription.source === SubscriptionSource.RECURLY
      && subscription.billingInterval === PlanInterval.MONTH
      && subscription.recurlySubUuId === product.recurlySubscriptionUuid);
};

export const hasSelectedPreviewRequest = (
  request: ProductPreviewRequest,
  currentSubscriptions: Subscription[] = [],
): boolean => request.quantity > 0
  || Object.values(request.addOns ?? {}).some(quantity => quantity > 0)
  || isMonthlyToAnnualSwitchRequest(request, currentSubscriptions);

export const getTrueBundleTitleId = (
  feature: Feature,
) => {
  if (TRUE_BUNDLE_PURCHASE_FEATURES.includes(feature)) {
    return getTrueBundlePlan(feature).titleId;
  }

  if (TRUE_MORE_TESTING_PRODUCT_FEATURES.includes(feature)) {
    return `feature.${feature.toLowerCase()}.product.name`;
  }

  return 'page.plan.v2.plan.title';
};

const buildLineItem = (
  planId: string,
  addOnCode: string,
  quantity: number,
  unitAmount: number,
): LineItem => ({
  addOnCode,
  displayName: addOnCode,
  discountAmount: 0,
  planId,
  prorationRate: 1,
  quantity,
  subtotal: quantity * unitAmount,
  tax: 0,
  taxRate: 0,
  unitAmount,
});

export const buildFallbackPreviewProduct = (
  request: ProductPreviewRequest,
  planPrice: SubscriptionPlanPrice | undefined,
): OrderProductPreview => {
  const feature = getPreviewRequestFeature(request);
  const billingInterval = PlanInterval.getIntervalFromPlanCode(request.productCode);
  const unitAmount = getUnitPrice(planPrice, feature, billingInterval, 'product');
  const baseLineItem = buildLineItem(
    request.productCode,
    request.productCode,
    request.quantity,
    unitAmount,
  );
  const addOnLineItems = Object.entries(request.addOns ?? {})
    .filter(([, quantity]) => quantity > 0)
    .map(([addOnCode, quantity]) => {
      const addOnFeature = getFeatureByAddOn(addOnCode.toLowerCase());
      const addOnUnitAmount = getUnitPrice(planPrice, addOnFeature, billingInterval, 'addOn');
      return buildLineItem(
        request.productCode,
        addOnCode.toLowerCase(),
        quantity,
        addOnUnitAmount,
      );
    });
  const addOnSubTotal = addOnLineItems
    .reduce((total, lineItem) => total + lineItem.subtotal, 0);

  return {
    accountId: 0,
    billingInterval,
    creditAmount: 0,
    discountAmount: 0,
    discountQuantity: 0,
    feature,
    lineItem: [
      baseLineItem,
      ...addOnLineItems,
    ],
    newQuantity: request.quantity,
    productCode: request.productCode,
    prorationRate: 1,
    quantity: request.quantity,
    subTotal: baseLineItem.subtotal + addOnSubTotal,
    tax: 0,
    taxRate: 0,
    total: baseLineItem.subtotal + addOnSubTotal,
    unitAmount,
  };
};

export const getMatchedPreviewProduct = (
  request: ProductPreviewRequest,
  response: OrderPreviewResponse | undefined,
): OrderProductPreview | undefined => {
  const requestedAddOns = Object.fromEntries(
    Object.entries(request.addOns ?? {})
      .filter(([, quantity]) => quantity > 0)
      .map(([addOnCode, quantity]) => [addOnCode.toLowerCase(), quantity]),
  );
  const requestedAddOnCount = Object.keys(requestedAddOns).length;

  return response?.productsPreview.find(product => {
    const paidAddOns = product.lineItem.filter(
      item => PAID_RECURLY_ADD_ONS.includes(item.addOnCode as RecurlyAddOn),
    );

    // BE may route an eligible account's legacy KSE code to its `_ecom_` sibling (product#1447),
    // so the response code is joined back to the request's legacy code ecom-insensitively.
    return isSamePlanIgnoringEcom(product.productCode, request.productCode)
      && product.newQuantity === request.quantity
      && (
        request.recurlySubscriptionUuid
        || product.quantity === request.quantity
      )
      && paidAddOns.length === requestedAddOnCount
      && paidAddOns.every(item => requestedAddOns[item.addOnCode] === item.quantity);
  });
};

export const hasPreviewResponseForRequests = (
  requests: ProductPreviewRequest[],
  response: OrderPreviewResponse | undefined,
): boolean => {
  if (requests.length === 0) {
    return true;
  }

  if (!response || response.productsPreview.length !== requests.length) {
    return false;
  }

  return requests.every(request => !!getMatchedPreviewProduct(request, response));
};

export const buildPreviewProducts = (
  requests: ProductPreviewRequest[],
  response: OrderPreviewResponse | undefined,
  planPrice: SubscriptionPlanPrice | undefined,
): OrderProductPreview[] => requests.map(request => {
  const matchedProduct = getMatchedPreviewProduct(request, response);
  return matchedProduct ?? buildFallbackPreviewProduct(request, planPrice);
});

export const getPaidAddOnLineItems = (
  products: OrderProductPreview[],
): LineItem[] => products.flatMap(
  product => product.lineItem.filter(
    it => Object.values(RecurlyAddOn).some(addOn => addOn === it.addOnCode)
      && it.addOnCode !== RecurlyAddOn.TESTCLOUD_SESSION_CROSSBROWSER_FREE,
  ),
);

const FREE_CROSS_BROWSER_SESSION_SEAT_STEP = 5;

export const getSelectedTrueBundleSeatQuota = (
  products: OrderProductPreview[],
): number => products
  .filter(product => TRUE_BUNDLE_PURCHASE_FEATURES.includes(
    getPreviewProductFeature(product),
  ))
  .reduce((sum, product) => sum + Math.max(product.newQuantity, 0), 0);

export const getPreviewTrueBundleSeatQuota = (
  products: OrderProductPreview[],
): number => products
  .filter(product => TRUE_BUNDLE_PURCHASE_FEATURES.includes(
    getPreviewProductFeature(product),
  ))
  .reduce((sum, product) => sum + Math.max(product.quantity, 0), 0);

const getCurrentPaidTrueBundleSeatQuota = (
  currentSubscriptions: Subscription[] = [],
): number => currentSubscriptions
  .filter(subscription => subscription.source === SubscriptionSource.RECURLY
    && TRUE_BUNDLE_PURCHASE_FEATURES.includes(subscription.feature))
  .reduce((sum, subscription) => sum + Math.max(subscription.quota, 0), 0);

const getPreviewExistingTrueBundleSeatQuota = (
  products: OrderProductPreview[],
): number => products
  .filter(product => TRUE_BUNDLE_PURCHASE_FEATURES.includes(
    getPreviewProductFeature(product),
  ))
  .reduce((sum, product) => sum + getPreviewExistingQuantity(product), 0);

export const getFinalTrueBundleSeatQuota = (
  products: OrderProductPreview[],
  currentSubscriptions: Subscription[] = [],
): number => {
  const currentTrueSeats = currentSubscriptions.length > 0
    ? getCurrentPaidTrueBundleSeatQuota(currentSubscriptions)
    : getPreviewExistingTrueBundleSeatQuota(products);

  return currentTrueSeats + getSelectedTrueBundleSeatQuota(products);
};

const getFreeCrossBrowserSessionQuota = (
  trueSeatQuota: number,
): number => (trueSeatQuota > 0
  ? Math.ceil(trueSeatQuota / FREE_CROSS_BROWSER_SESSION_SEAT_STEP)
  : 0);

export const getCloudSessionQuota = (
  products: OrderProductPreview[],
  currentSubscriptions: Subscription[] = [],
): number => {
  const finalTrueSeats = getFinalTrueBundleSeatQuota(products, currentSubscriptions);

  if (finalTrueSeats <= 0) {
    return 0;
  }

  const currentTrueSeats = currentSubscriptions.length > 0
    ? getCurrentPaidTrueBundleSeatQuota(currentSubscriptions)
    : getPreviewExistingTrueBundleSeatQuota(products);

  return Math.max(
    getFreeCrossBrowserSessionQuota(finalTrueSeats)
      - getFreeCrossBrowserSessionQuota(currentTrueSeats),
    0,
  );
};

export const getFeatureQuota = (
  products: OrderProductPreview[],
): Partial<Record<Feature, number>> => {
  const featureQuota: Partial<Record<Feature, number>> = {};
  products.forEach(product => {
    featureQuota[getPreviewProductFeature(product)] = product.newQuantity;
  });
  getPaidAddOnLineItems(products).forEach(item => {
    featureQuota[getFeatureByAddOn(item.addOnCode)] = item.quantity;
  });
  return featureQuota;
};
